<template>
    <Dialog
        :value="true"
        :header-text="$t('Agreements')"
        :width="920"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <v-row>
            <v-col cols="12">
                <div class="mb-6">
                    <div class="caption g5--text">{{ $t('Member') }}</div>
                    {{ personName }}
                </div>
                <ExpansionPanels
                    :items="visibleAgreements"
                    :title="formatTitle"
                    :errors="errors"
                    :value="openExpansionPanels"
                    @change="openExpansionPanels = $event"
                >
                    <template v-slot:actions="{ item }">
                        <icon-button @click="onDelete(item)">
                            mdi-delete
                        </icon-button>
                    </template>
                    <template v-slot:default="{ item, index }">
                        <MainFlextimeAgreementContent
                            :key="keyHack"
                            :item="item"
                            :index="index"
                            :errors="errors"
                            @update-errors="errors = $event"
                            @update-item-start-date="item.date_from = $event"
                            @update-item-date-until="item.date_until = $event"
                            @update-item-working-hour="item._data[$event.key] = $event.value"
                            @update-item-work-holidays="item.work_holidays = $event"
                        />
                    </template>
                </ExpansionPanels>
            </v-col>
        </v-row>
        <template slot="footer">
            <v-btn class="primary--text" text @click="addNewAgreement">
                <v-icon color="primary" left>
                    mdi-plus
                </v-icon>
                {{ $t('Add agreement') }}
            </v-btn>
            <v-spacer />
            <secondary-button @click="$emit('close')">
                {{ $t('Cancel') }}
            </secondary-button>
            <primary-button @click="onSave">
                {{ $t('Save') }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import { $t } from "@/i18n";
import {ref, reactive, computed} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import ApiAccountMemberSettings from "@/api/accountMember/settings";
import {
    formatTimeDuration,
    parseTimeDuration,
    formatFlextimeDuration,
    formatDate,
    parseSysDate,
    parseSysDateTime,
    formatSysDate
} from "@/plugins/formatting";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import Loader from "@/utility/loader";
import ExpansionPanels from "@/components/ExpansionPanels/Index";
import ValidationModule from "@/modules/validation/validation"
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import MainFlextimeAgreementContent from "@/components/Flextime/Agreement/MainFlextimeAgreementContent.vue";

export default {
    name: "FlextimeAgreementsModal",
    components: {
        Dialog,
        ExpansionPanels,
        MainFlextimeAgreementContent
    },
    props: {
        companyId: {
            type: Number,
            required: true
        },
        personId: {
            type: Number,
            required: true
        },
        personName: {
            type: String,
            required: true
        }
    },
    setup(props, ctx) {
        const loader = reactive(new Loader());
        // When adding new agreement then custom is selected, ugly hack
        const keyHack = ref(false)
        const agreements = ref([]);
        const errors = ref({});
        const { showFeedbackNotification } = FeedbackNotificationModule();
        const openExpansionPanels = ref([]);

        const loadData = async () => {
            await loader.withLoader(async () => {
                try {
                    let data = await ApiAccountMemberSettings.getPersonAccountMemberSettings(props.personId, props.companyId)
                    agreements.value = data.map(x => {
                        if (!x._data) {
                            x._data = {}
                        }

                        if (!x._data.monday) {
                            x._data.monday = 0
                        }

                        if (!x._data.tuesday) {
                            x._data.tuesday = 0
                        }

                        if (!x._data.wednesday) {
                            x._data.wednesday = 0
                        }

                        if (!x._data.thursday) {
                            x._data.thursday = 0
                        }

                        if (!x._data.friday) {
                            x._data.friday = 0
                        }

                        if (!x._data.saturday) {
                            x._data.saturday = 0
                        }

                        if (!x._data.sunday) {
                            x._data.sunday = 0
                        }

                        x.is_deleted = 0;
                        x.date_from = formatSysDate(parseSysDateTime(x.date_from))
                        x.date_until = x.date_until ? formatSysDate(parseSysDateTime(x.date_until)) : null

                        return x
                    })

                    if (!agreements.value.length) {
                        addNewAgreement()
                    }

                    openExpansionPanels.value = [0]
                } catch (e) {
                    errorDialog.showError(e)
                }
            })
        }

        const addNewAgreement = () => {
            agreements.value.unshift({
                company_id: props.companyId,
                cost_price: 0,
                created_at: null,
                is_deleted: 0,
                date_from: null,
                date_until: null,
                id: null,
                person_id: props.personId,
                sales_price: 0,
                type: "weekly",
                updated_at: null,
                work_holidays: 0,
                _data: {
                    'monday': 28800,
                    'tuesday': 28800,
                    'wednesday': 28800,
                    'thursday': 28800,
                    'friday': 28800,
                    'saturday': 0,
                    'sunday': 0
                }
            })

            keyHack.value = !keyHack.value
            openExpansionPanels.value = [0]
        }

        const formatTitle = (item) => {
            let output = ""
            if (!item.id) {
                return $t('New agreement')
            }

            if (item.date_from) {
                output = formatDate(parseSysDate(item.date_from)) + " - "
            }

            if (item.date_until) {
                output += formatDate(parseSysDate(item.date_until))
            } else {
                output += "..."
            }

            return output
        }

        const onSave = async () => {
            try {
                await ApiAccountMemberSettings.savePersonAccountMemberSettings(props.personId, props.companyId, agreements.value)
                showFeedbackNotification($t('Settings saved'))
                ctx.emit('close')
                ctx.emit('update')
            } catch (e) {
                errors.value = ValidationModule.parseErrors(e)
                if (!ValidationModule.hasErrors(errors.value)) {
                    errorDialog.showError(e)
                }
            }
        }

        const onDelete = item => {
            item.is_deleted = 1
            errors.value = {}
        }

        const visibleAgreements = computed(() => agreements.value.filter(x => !x.is_deleted))

        loadData()

        return {
            loader,
            formatTimeDuration,
            formatFlextimeDuration,
            agreements,
            addNewAgreement,
            formatTitle,
            onSave,
            visibleAgreements,
            parseTimeDuration,
            openExpansionPanels,
            ValidationModule,
            errors,
            onDelete,
            keyHack
        }
    }
}
</script>
